import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import { isValidHumanAmount } from '@/helpers/amount'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildTransferMessage,
  TransferFormValues,
} from './transferMessageBuilder'

const transferSchema = z.object({
  fromAddress: z.string().min(1, 'From address is required'),
  toAddress: z.string().min(1, 'To address is required'),
  amount: z
    .string()
    .min(1, 'Amount is required')
    .refine(value => isValidHumanAmount(value, 18), {
      message: 'Invalid amount format',
    }),
  denom: z.string().min(1, 'Denom is required'),
})

export default function TransferForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<TransferFormValues>({
    resolver: zodResolver(transferSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      fromAddress: '',
      toAddress: '',
      amount: '',
      denom: 'abridge',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('fromAddress', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildTransferMessage(watchedValues as TransferFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Send Tokens Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to send tokens from one address to another
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='fromAddress'>From Address</UiLabel>
          <UiInput
            id='fromAddress'
            {...form.register('fromAddress')}
            placeholder='Group account (auto-filled)'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.fromAddress && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.fromAddress.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Transfer Details</h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='toAddress'>To Address</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='toAddress'
                  {...form.register('toAddress')}
                  placeholder='bridge1...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.toAddress && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.toAddress.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='amount'>
                Amount in BRIDGE (Human Readable)
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='amount'
                  {...form.register('amount')}
                  placeholder='1.5'
                  disabled={isSubmitting}
                  type='text'
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.amount && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.amount.message}
                </p>
              )}
              <p className='text-muted-foreground text-xs'>
                Enter amount in human-readable format (e.g., 1.5 for 1.5 tokens)
              </p>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='denom'>Denomination</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='denom'
                  {...form.register('denom')}
                  placeholder='abridge'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.denom && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.denom.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'transfer-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildTransferMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
